/* @flow */

import * as React from 'react'

import { createApiHook, createDataCache } from '../../app/hooks'
import { getCountries, getStates } from './api'

import * as locales from '../../messages'

const fetchCountriesForHook = () =>
  getCountries().then(response => {
    if (!response.error) {
      return {
        entity: response.payload,
      }
    } else {
      return response
    }
  })

const useCountries = createApiHook(fetchCountriesForHook, [])

const {
  cache: { clearCache: clearCountriesCache },
  hook: useCachedCountries,
} = createDataCache(useCountries)

const fetchStatesForHook = () =>
  getStates().then(response => {
    if (!response.error) {
      return {
        entity: response.payload,
      }
    } else {
      return response
    }
  })

const useStates = createApiHook(fetchStatesForHook, [])

const {
  cache: { clearCache: clearStatesCache },
  hook: useCachedStates,
} = createDataCache(useStates)

export {
  // countries
  clearCountriesCache,
  getCountries,
  useCountries,
  useCachedCountries,
  // states
  useCachedStates,
  clearStatesCache,
}

// Create a context for locale management
const LocaleContext = React.createContext({
  locale: 'en',
  setLocale: (locale: string) => {},
})

// Store a reference to the context setter so it can be called from outside React
let globalLocaleSetterRef: ?(locale: string) => void = null

// Provider component to wrap the app
export const LocaleProvider = ({
  children,
}: {
  children: React.Node,
}): React.Node => {
  const [locale, setLocale] = React.useState('en')

  // Store the setter reference for global access
  React.useEffect(() => {
    globalLocaleSetterRef = setLocale
    return () => {
      globalLocaleSetterRef = null
    }
  }, [setLocale])

  const value = React.useMemo(
    () => ({
      locale,
      setLocale,
    }),
    [locale]
  )

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  )
}

// Hook to access the current locale
export const useLocale = (): ({
  locale: string,
  setLocale: (locale: string) => void,
}) => {
  const context = React.useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}

// Backward compatibility - keep module level variable for non-hook usage
let currentLocale = 'en'

export const setCurrentLocale = locale => {
  currentLocale = locale

  // Also update the React context if available
  if (globalLocaleSetterRef) {
    globalLocaleSetterRef(locale)
  }
}

export const getCurrentLocale = () => {
  return currentLocale
}

export const useOverrideLocale = locale => {
  const { setLocale } = useLocale()

  React.useEffect(() => {
    if (!locale) {
      return
    }

    // Save the previous locale
    const previous = getCurrentLocale()

    // Set the new locale in both context and module variable
    setLocale(locale)
    setCurrentLocale(locale)

    // On cleanup, restore the previous locale
    return () => {
      setLocale(previous)
      setCurrentLocale(previous)
    }
  }, [locale, setLocale])
}

// Helper for nested key lookup
const getTranslationKey = (obj, path) => {
  return path
    .split('.')
    .reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj)
}

export const t = (key, fallback = '', variables = {}) => {
  const locale = getCurrentLocale()
  let value = getTranslationKey(locales[locale], key)
  if (value === undefined) {
    value = fallback
  }

  // If variables are provided, replace placeholders
  if (variables && typeof variables === 'object') {
    for (const [varKey, varValue] of Object.entries(variables)) {
      value = value.replace(new RegExp(`\\{${varKey}\\}`, 'g'), varValue)
    }
  }

  return value
}

// Hook version of t function that subscribes to locale changes
export const useTranslation = () => {
  const { locale } = useLocale()

  return React.useCallback(
    (key, fallback = '', variables = {}) => {
      let value = getTranslationKey(locales[locale], key)
      if (value === undefined) {
        value = fallback
      }

      // If variables are provided, replace placeholders
      if (variables && typeof variables === 'object') {
        for (const [varKey, varValue] of Object.entries(variables)) {
          value = value.replace(new RegExp(`\\{${varKey}\\}`, 'g'), varValue)
        }
      }

      return value
    },
    [locale]
  )
}
